import type { PublicAsset } from '../../../shared/lib';
import type { PublicService } from '../publicApi';

export interface ServiceCardProps {
  service: PublicService;
  icon: PublicAsset | undefined;
}

/**
 * The mono glyph tiles from the Bitwize landing mock, used only when a
 * service has no icon uploaded — an editor who sets one still gets theirs.
 *
 * Which glyph a service gets is derived from its slug rather than its
 * position in the list, so a card keeps the same mark when the grid is
 * reordered or filtered. They are decorative and `aria-hidden`: `{ }` next
 * to "Custom Software Development" tells a screen-reader user nothing the
 * heading has not already said.
 */
const GLYPHS = ['{ }', '</>', '[ ]', '~/', '0x', '::'] as const;

function glyphFor(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) hash = (hash * 31 + slug.charCodeAt(i)) % 4096;
  return GLYPHS[hash % GLYPHS.length]!;
}

/**
 * A service card is read-only. It is never a link.
 *
 * It used to link to a detail page whenever the API said the service had
 * one — `has_detail_page`, which is true exactly when that locale has a
 * long description. That produced a grid where some cards opened and some
 * did not, with nothing on the card to say which was which: the same
 * component, the same shape, one clickable and one inert depending on a
 * field an editor filled in months ago. A visitor reads that as a broken
 * link, not as a content decision.
 *
 * So the whole grid is one thing now — a card you read. The API still
 * reports `has_detail_page` and the detail route still resolves for anyone
 * holding the URL; nothing on the public site offers it.
 */
export function ServiceCard({ service, icon }: ServiceCardProps) {
  return (
    <div className="hs-card flex flex-col gap-2.5 rounded-[var(--hs-card-radius)] p-[var(--hs-card-padding)]">
      {icon ? (
        <img src={icon.url} alt="" className="size-10" aria-hidden="true" />
      ) : (
        <span
          aria-hidden="true"
          dir="ltr"
          className="flex size-9.5 items-center justify-center rounded-[10px] bg-accent-subtle font-mono text-sm text-accent"
        >
          {glyphFor(service.slug)}
        </span>
      )}
      <h3 className="font-semibold text-text">{service.title}</h3>
      {service.short_description ? (
        <p className="line-clamp-3 text-sm text-text-muted">{service.short_description}</p>
      ) : null}
      {service.features?.length ? (
        <ul className="mt-1 flex flex-col gap-1 text-sm text-text-muted">
          {service.features.slice(0, 4).map((feature, i) => (
            <li key={i} className="flex items-start gap-2">
              <span aria-hidden="true">•</span>
              {feature}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
